import { FormControl, InputLabel, OutlinedInput, Input, TextFieldProps } from '@mui/material';
import FormLabel from './FormLabel';
import classNames from 'classnames/bind';
import { HTMLAttributes, KeyboardEvent, KeyboardEventHandler, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { AppInputCustomProps, CustomFieldProps } from './types';
import { IMaskInput } from 'react-imask';
import useDebouncer from '@hooks/useDebounce';
import ClearInputAdornment from './ClearInputAdornment';

import styles from './Form.module.css';
const classNamesCtx = classNames.bind(styles);

type AppNumberFieldProps = Omit<TextFieldProps, 'onKeyDown'> &
  AppInputCustomProps & {
    /** Override the expected onchange arguments to manually invoke with accepted value */
    onValueChange: (value: number | null) => void;
    /** Key down handler targeting the inner input element */
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  };

type NumberMaskBaseProps = HTMLAttributes<HTMLInputElement> & {
  /** Required input name. Based HTML Input Element is optional. */
  name: string;
  /** Input value */
  value: string;
  /** Override the expected onchange arguments to manually invoke with accepted value */
  onValueChange: (value: number | null) => void;
  /** Flag indicating the input is in a disabled state. Default: false */
  disabled?: boolean;
  /** Max value allowed in the input. Default: none */
  maxValue?: number;
  /** Number of decimal places allowed. Default: 0 */
  precision?: number;
  /** Flag allowing the input to accept numbers below 0. Default: false */
  allowNegativeNumbers?: boolean;

  placeholder?: string;
  /** Flag indicating cursor uses INSERT behavior, writing over existing text in the input. Default: false */
  overwrite?: boolean;
};

/** React iMask Input Component applying the Phone Number Mask as (###) ###-#### */
const NumberMaskCustom = forwardRef<HTMLInputElement, NumberMaskBaseProps>(function RenderTextMaskCustom(
  { value, precision, allowNegativeNumbers, ...props },
  ref
) {
  const maskOptions = useMemo(
    () => ({
      mask: Number,
      thousandsSeparator: ',',
      scale: precision ?? 0,
      radix: '.',
      signed: allowNegativeNumbers ?? false, // Allow Negative Numbers
      padFractionalZeros: true // Pad ending with 0s when applicable
    }),
    [allowNegativeNumbers, precision]
  );

  const displayValue = useMemo(() => {
    /** If we are not allowing decimal places to display, drop those digits so they
     * are not incorrectly displayed. (IE: 0.45 would end up displaying 45)	 */
    if (value && !precision) {
      return String(Math.trunc(Number(value)));
    }
    return value;
  }, [precision, value]);

  return (
    <IMaskInput
      autoComplete='one-time-code'
      id={props.name}
      name={props.name}
      type='text'
      className={props.className}
      value={displayValue}
      disabled={props.disabled}
      placeholder={props.placeholder}
      mask={'num'}
      blocks={{
        num: maskOptions
      }}
      unmask
      inputRef={ref}
      onBlurCapture={props.onBlur}
      onAccept={(value: string) => {
        if (value === '') {
          props.onValueChange(null);
        } else if (!isNaN(Number(value))) {
          if (props.maxValue && Number(value) > props.maxValue) {
            props.onValueChange(props.maxValue);
          } else {
            /**
             * If the input allows for negative numbers, when the user begins with a '-' do not call the
             * value change callback otherwise it will reset to 0 because -0 is not a Number. Wait for the next
             * input key down to actually change the value.
             */
            if (allowNegativeNumbers && value === '-0') {
              return;
            }
            props.onValueChange(Number(value));
          }
        }
      }}
      onBlur={props.onBlur}
      onKeyDown={props.onKeyDown}
      overwrite={props.overwrite ?? false}
    />
  );
});

/**
 * Wrapper around MUI TextField to provide application-wide default behaviors.
 */
export function NumberField({ customProps, focused, ...props }: AppNumberFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const textFieldClass = classNamesCtx({
    input: true,
    dirty: customProps.isDirty,
    error: props.error,
    disabled: props.disabled,
    ['cell-input']: customProps.enableCellStyles,
    [props.className ?? '']: Boolean(props.className)
  });

  const inputContainerClass = classNamesCtx({
    root: true,
    ['number-input-container']: true,
    ['label-margin']: Boolean(customProps.labelText),
    disabled: props.disabled,
    [customProps.extendedClassName ?? '']: Boolean(customProps.extendedClassName)
  });

  const InputComponent = props.variant === 'standard' ? Input : OutlinedInput;

  useEffect(() => {
    if (focused) {
      inputRef.current?.focus();
    }
  }, [focused]);

  return (
    <FormControl
      variant={props.variant}
      className={textFieldClass}>
      {customProps.labelText && (
        <InputLabel htmlFor={props.name}>
          <FormLabel
            text={customProps.labelText}
            error={props.error && !props.disabled}
            errorText={customProps.errorText}
            required={customProps.required}
          />
        </InputLabel>
      )}
      <InputComponent
        title={customProps.disabledReason}
        className={inputContainerClass}
        id={props.name}
        inputComponent={NumberMaskCustom as never}
        placeholder={props.placeholder}
        disabled={props.disabled}
        inputProps={{
          onBlur: props.onBlur,
          onKeyDown: props.onKeyDown,
          value: props.value,
          onValueChange: props.onValueChange,
          name: props.name,
          disabled: props.disabled,
          maxValue: customProps.maxValue,
          className: styles['number-input'],
          precision: customProps.precision,
          allowNegativeNumbers: customProps.allowNegativeNumbers,
          ref: inputRef
        }}
        label={`${customProps.labelText} ${props.error ? 'er' : ''}`} // This is used for the break in the fieldset, since we add an error icon when it is an error add extra space
        error={props.error}
        startAdornment={customProps.startAdornment}
        endAdornment={
          <>
            {customProps.endAdornment}
            {!customProps.hideClearIcon && (
              <ClearInputAdornment
                onClear={() => {
                  customProps.onClear?.();
                  inputRef.current?.focus();
                }}
                inputValue={props.value}
                inputIsDisabled={props.disabled}
              />
            )}
          </>
        }
        autoComplete='one-time-code'
      />
    </FormControl>
  );
}

function asNumberInput() {
  const Component = ({ field, form, ...props }: CustomFieldProps) => {
    const { value, initialValue, error, touched } = form.getFieldMeta<number | null>(field.name);

    const isDirty = useMemo(() => {
      if (form.dirty) {
        return value !== initialValue;
      }
      return false;
    }, [value, initialValue, form.dirty]);

    /** Keeping track of the input state locally for performance reasons when typing into the input */
    const [myValue, setMyValue] = useState<number | null>(() => value ?? null);

    /** If the formik value changes outside of this component, update our local state to reflect */
    useEffect(() => {
      setMyValue((state) => {
        const formValue = value ?? null;
        if (formValue !== state) {
          return formValue;
        }
        return state;
      });
    }, [value]);

    /** When the local state has changed, compare to the form value. After 300ms update the form if the value differs. */
    const flushDebounce = useDebouncer(
      () => {
        const formValue = value ?? null;
        if (myValue !== formValue) {
          void form.setFieldTouched(field.name, true);
          void form.setFieldValue(field.name, myValue, true);
          props.onChangeExtension?.(myValue);
        }
      },
      [myValue],
      300
    );

    /** On Enter, flush the pending debounced value to Formik synchronously so the submit handler sees the current value */
    const handleKeyDown = useCallback(
      (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          flushSync(() => {
            flushDebounce();
          });
        }
      },
      [flushDebounce]
    );

    return (
      <NumberField
        name={field.name}
        placeholder={props.placeholder}
        variant={props.variant}
        className={props.className}
        value={myValue != null ? String(myValue) : ''}
        onValueChange={(value: number | null) => {
          setMyValue(value);
        }}
        onKeyDown={handleKeyDown}
        disabled={form.isSubmitting || props.disabled}
        error={touched && Boolean(error)}
        customProps={{
          labelText: props.label ?? '',
          isDirty: isDirty,
          errorText: error,
          onClear: () => {
            setMyValue(null);
            props.onClearExtension?.();
          },
          disabledReason: props.disabledReason,
          maxValue: props.max,
          precision: props.precision,
          startAdornment: props.startAdornmentIcon,
          endAdornment: props.endAdornmentIcon,
          allowNegativeNumbers: props.allowNegativeNumbers ?? false,
          required: props.required
        }}
      />
    );
  };
  Component.displayName = 'FormikNumberInput';
  return Component;
}

export const FormikNumberInput = asNumberInput();
